import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";
import { Button, Divider, Form, Loader, Modal } from "semantic-ui-react";
import { validators } from "not-valid";

import { GENDER_OPTIONS } from "@teamprevent/common/utils";
import { toast } from "@teamprevent/common/toasts";
import { Input } from "@neworbit/simpleui-input";

import { ApplicationState } from "../../../../applicationState";
import { EditEmployeeRecordFromOhCaseModel } from "../../../employeeRecord/model";
import { Customer } from "../../../customer";
import { saveEmployeeRecordFromOhCase } from "../../../employeeRecord/actions";

interface Props {
    customer: Customer;
    employeeRecordId: string;
}

export const EmployeeRecordDetails: React.FunctionComponent<Props> = (props: Props) => {

    const [viewDetailsOpen, setViewDetailsOpen] = React.useState<boolean>(false);
    const [editOpen, setEditOpen] = React.useState<boolean>(false);
    const [submitted, setSubmitted] = React.useState<boolean>(false);
    const [saving, setSaving] = React.useState<boolean>(false);
    const [model, setModel] = React.useState<EditEmployeeRecordFromOhCaseModel>(null);

    const employeeRecord = useSelector((state: ApplicationState) =>
        state.employeeRecordsDetail?.employeeRecords?.find(er => er.id === props.employeeRecordId));

    React.useEffect(() => {
        if (employeeRecord) {
            setModel({
                id: employeeRecord.id ?? "",
                title: employeeRecord.title ?? "",
                firstName: employeeRecord.firstName ?? "",
                lastName: employeeRecord.lastName ?? "",
                gender: employeeRecord.gender ?? "",
                dateOfBirth: employeeRecord.dateOfBirth ?? null,
                personalEmailAddress: employeeRecord.personalEmailAddress ?? "",
                mobilePhoneNumber: employeeRecord.mobilePhoneNumber ?? "",
                addressLine1: employeeRecord.addressLine1 ?? "",
                addressLine2: employeeRecord.addressLine2 ?? "",
                addressLine3: employeeRecord.addressLine3 ?? "",
                city: employeeRecord.city ?? "",
                county: employeeRecord.county ?? "",
                postCode: employeeRecord.postCode ?? "",
                jobTitle: employeeRecord.jobTitle ?? "",
            });
        }
    }, [employeeRecord, props.customer]);

    const dispatch = useDispatch();

    const isModelValid = (erModel: EditEmployeeRecordFromOhCaseModel) => {
        return erModel.title && erModel.firstName && erModel.lastName && erModel.gender && erModel.dateOfBirth && erModel.dateOfBirth.isValid();
    };

    const onEdit = () => {
        setEditOpen(true);
        setModel({
            id: employeeRecord.id ?? "",
            title: employeeRecord.title ?? "",
            firstName: employeeRecord.firstName ?? "",
            lastName: employeeRecord.lastName ?? "",
            gender: employeeRecord.gender ?? "",
            dateOfBirth: employeeRecord.dateOfBirth ?? null,
            personalEmailAddress: employeeRecord.personalEmailAddress ?? "",
            mobilePhoneNumber: employeeRecord.mobilePhoneNumber ?? "",
            addressLine1: employeeRecord.addressLine1 ?? "",
            addressLine2: employeeRecord.addressLine2 ?? "",
            addressLine3: employeeRecord.addressLine3 ?? "",
            city: employeeRecord.city ?? "",
            county: employeeRecord.county ?? "",
            postCode: employeeRecord.postCode ?? "",
            jobTitle: employeeRecord.jobTitle ?? "",
        });
    };

    const onSave = async () => {
        setSubmitted(true);

        if (!isModelValid(model)) {
            return;
        }

        setSaving(true);

        await dispatch(saveEmployeeRecordFromOhCase(props.customer.id, model));

        setSaving(false);
        setEditOpen(false);
        setViewDetailsOpen(false);

        toast.success("Employee record has been updated.");
    };

    const onTitleChange = (title: string) => {
        setModel({ ...model, title });
    };

    const onFirstNameChange = (firstName: string) => {
        setModel({ ...model, firstName });
    };

    const onLastNameChange = (lastName: string) => {
        setModel({ ...model, lastName });
    };

    const onGenderChange = (gender: string) => {
        setModel({ ...model, gender });
    };

    const onDateOfBirthChange = (dateOfBirth: moment.Moment) => {
        setModel({ ...model, dateOfBirth });
    };

    const onPersonalEmailAddressChange = (personalEmailAddress: string) => {
        setModel({ ...model, personalEmailAddress });
    };

    const onMobilePhoneNumberChange = (mobilePhoneNumber: string) => {
        setModel({ ...model, mobilePhoneNumber });
    };

    const onAddressLine1Change = (addressLine1: string) => {
        setModel({ ...model, addressLine1 });
    };

    const onAddressLine2Change = (addressLine2: string) => {
        setModel({ ...model, addressLine2 });
    };

    const onAddressLine3Change = (addressLine3: string) => {
        setModel({ ...model, addressLine3 });
    };

    const onCityChange = (city: string) => {
        setModel({ ...model, city });
    };

    const onCountyChange = (county: string) => {
        setModel({ ...model, county });
    };

    const onPostCodeChange = (postCode: string) => {
        setModel({ ...model, postCode });
    };

    const onJobTitleChange = (jobTitle: string) => {
        setModel({ ...model, jobTitle });
    };

    if (!model) {
        return <Loader active />;
    }

    return (
        <>
            <Modal open={viewDetailsOpen}>
                <Modal.Header content="Employee Record Details" />
                <Modal.Content>
                    <Form>
                        <Input.Text
                            readOnly
                            value={model.firstName}
                            label="First Name"
                        />
                        <Divider hidden />
                        <Input.Text
                            readOnly
                            value={model.lastName}
                            label="Last Name"
                        />
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button content="Cancel" onClick={() => setViewDetailsOpen(false)} disabled={saving} />
                    <Button icon="edit" content="Edit" onClick={onEdit} disabled={saving} />
                </Modal.Actions>
            </Modal>
            <Modal open={editOpen}>
                <Modal.Header content="Edit Employee Record Details" />
                <Modal.Content>
                    <Form>
                        <Input.Text
                            value={model.title}
                            label="Title *"
                            required
                            showErrors={submitted}
                            onChange={onTitleChange}
                        />
                        <Divider hidden />
                        <Input.Text
                            value={model.firstName}
                            label="First Name *"
                            required
                            showErrors={submitted}
                            onChange={onFirstNameChange}
                        />
                        <Divider hidden />
                        <Input.Text
                            value={model.lastName}
                            label="Last Name *"
                            required
                            showErrors={submitted}
                            onChange={onLastNameChange}
                        />
                        <Input.Dropdown
                            label="Gender *"
                            placeholder="Please select"
                            value={model.gender}
                            showErrors={submitted}
                            required
                            options={GENDER_OPTIONS}
                            onChange={onGenderChange}
                        />
                        <Divider hidden />
                        <Input.Email
                            label="Personal Email Address"
                            value={model.personalEmailAddress}
                            showErrors={submitted}
                            onChange={onPersonalEmailAddressChange}
                        />
                        <Divider hidden />
                        <Input.Date
                            label={"Date Of Birth *"}
                            value={model.dateOfBirth}
                            showErrors={submitted}
                            required
                            onChange={onDateOfBirthChange}
                        />
                        <Divider hidden />
                        <Input.Text
                            label="Mobile Phone Number"
                            value={model.mobilePhoneNumber}
                            showErrors={submitted}
                            validation={[validators.validPhoneNumber(10)]}
                            onChange={onMobilePhoneNumberChange}
                        />
                        <Divider hidden />
                        <Input.Text
                            label="Address Line 1"
                            value={model.addressLine1}
                            showErrors={submitted}
                            onChange={onAddressLine1Change}
                        />
                        <Divider hidden />
                        <Input.Text
                            label="Address Line 2"
                            value={model.addressLine2}
                            showErrors={submitted}
                            onChange={onAddressLine2Change}
                        />
                        <Divider hidden />
                        <Input.Text
                            label="Address Line 3"
                            value={model.addressLine3}
                            showErrors={submitted}
                            onChange={onAddressLine3Change}
                        />
                        <Divider hidden />
                        <Input.Text
                            label="City"
                            value={model.city}
                            showErrors={submitted}
                            onChange={onCityChange}
                        />
                        <Divider hidden />
                        <Input.Text
                            label="County"
                            value={model.county}
                            showErrors={submitted}
                            onChange={onCountyChange}
                        />
                        <Divider hidden />
                        <Input.Text
                            label="Post Code"
                            value={model.postCode}
                            showErrors={submitted}
                            onChange={onPostCodeChange}
                        />
                        <Divider hidden />
                        <Input.Text
                            label="Job Title"
                            value={model.jobTitle}
                            showErrors={submitted}
                            onChange={onJobTitleChange}
                        />
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button content="Cancel" onClick={() => setEditOpen(false)} disabled={saving} />
                    <Button content="Save" onClick={onSave} disabled={saving} />
                </Modal.Actions>
            </Modal>
            <Button icon="address book" onClick={() => setViewDetailsOpen(true)} />
        </>
    );
};
